import React, { useRef } from 'react';
import {
  Col,
  Form,
  FormItemProps,
  SelectProps,
  Row,
  Select,
  Typography,
  Tooltip,
} from 'antd';
import {
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import classNames from 'classnames';

type Props<T extends FieldValues> = {
  selectProps?: SelectProps<any>;
  controller: UseControllerProps<T>;
  errorCol?: 12 | 24;
  showErrorTooltip?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
} & Omit<FormItemProps, 'children'>;

function InputField<T extends FieldValues>(props: Props<T>) {
  const {
    selectProps,
    controller,
    errorCol = 24,
    label,
    onBlur,
    onFocus,
    ...rest
  } = props;
  const { fieldState, field } = useController<T>(controller);
  const { error } = fieldState;
  const inputRef = useRef<any>(null);
  const valueError =
    typeof error === 'object' && error !== null && 'value' in error
      ? (error as any).value
      : error;
  const {
    className: selectClassName,
    style: selectStyle,
    ...restSelectProps
  } = selectProps || {};
  const showErrorTooltip = props.showErrorTooltip ?? false;
  const showTooltip = showErrorTooltip && !!error;

  const handleClearInput = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const selectNode = (
    <Select
      {...restSelectProps}
      {...field}
      ref={inputRef}
      onChange={(val) => {
        field.onChange(val ?? '');
      }}
      onBlur={() => {
        onBlur && onBlur();
      }}
      onFocus={() => {
        onFocus && onFocus();
      }}
      onClear={handleClearInput}
      style={{ width: '100%', ...selectStyle }}
      className={classNames(selectClassName, {
        'form-tooltip-error': showTooltip,
      })}
    />
  );

  const fieldContent = showTooltip ? (
    <Tooltip
      placement="topLeft"
      title={valueError?.message}
      overlayClassName="form-tooltip-error-tooltip"
    >
      <span className="form-tooltip-wrapper">{selectNode}</span>
    </Tooltip>
  ) : (
    selectNode
  );

  return (
    <Form.Item
      colon={false}
      label={label}
      labelAlign="left"
      labelCol={label ? { span: 24 } : undefined}
      wrapperCol={label ? { span: 24 } : undefined}
      validateStatus={error ? 'error' : ''}
      {...rest}
    >
      <Row gutter={4}>
        <Col span={errorCol}>
          <Row gutter={8}>
            <Col span={24}>{fieldContent}</Col>
          </Row>
        </Col>
        {!showTooltip && error && (
          <Col span={errorCol}>
            <Typography.Text
              className="ant-form-item-explain ant-form-item-explain-error"
              type="danger"
              ellipsis={{ tooltip: true }}
              style={{ lineHeight: errorCol === 24 ? '16px' : '42px' }}
            >
              {valueError?.message}
            </Typography.Text>
          </Col>
        )}
      </Row>
    </Form.Item>
  );
}

export default InputField;
